import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDarkMode } from './DarkModeContext';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';
const NAVY = '#1a2744';
const GOLD = '#c9a84c';
// LEAVECANCEL: the amber the portal already uses for a warning/pending state (the sync badge's
// "pending" and the leaver warnings both use this pair). Reused rather than a new colour so a
// cancellation reads as the same KIND of thing the rest of the portal calls "needs attention".
const WARN_BG = '#fef3c7';
const WARN_FG = '#92400e';

function fmt(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

// LEAVECANCEL: the second section of the Validation page (see ValidationView.js, which was built
// expecting exactly this - "future approval types are added here as additional sections").
//
// DISTINGUISHABLE FROM A NEW LEAVE REQUEST AT A GLANCE, which is the whole point of it being its
// own section rather than extra rows in the holiday list. Four separate signals, so the difference
// survives losing any one of them:
//   1. ITS OWN SECTION with its own heading, "Cancellation requests awaiting your decision",
//      physically separate from "Holiday approvals awaiting your decision" above it.
//   2. A "Cancellation" BADGE on every row, in the portal's amber warning pair - a colour the
//      approvals section never uses.
//   3. THE LEAVE TYPE named on the row (Holiday / WFH), which the approvals list does not show
//      because everything in it is a holiday by definition. A cancellation can be either.
//   4. THE BUTTONS SAY WHAT THEY DO to the cancellation, not to the leave: "Approve cancellation"
//      and "Keep the leave" rather than the bare Approve/Reject of a new request. "Keep the leave"
//      is deliberately not called "Reject" - refusing a cancellation means the leave STANDS, and
//      a manager scanning quickly should not have to work out which way round the double negative
//      goes.
export default function LeaveCancellationsSection() {
  const { isDarkMode } = useDarkMode();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);
  const [error, setError] = useState('');
  // Same split as HolidayApprovalsSection: a failed LOAD must suppress the empty state, so a
  // fetch failure is never mistaken for a genuinely empty queue.
  const [loadFailed, setLoadFailed] = useState(false);

  const cardBg = isDarkMode ? '#1a2744' : '#ffffff';
  const textCol = isDarkMode ? '#e8e4d8' : '#1a1a2e';
  const mutedCol = isDarkMode ? '#9ba8c0' : '#666';
  const border = isDarkMode ? '1px solid rgba(201,168,76,0.25)' : '1px solid #e2e6ec';
  const rowBorder = isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f0f0f0';

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/leave/pending-cancellations`)
      .then(res => { setRequests(res.data); setError(''); setLoadFailed(false); })
      .catch(() => {
        setRequests([]);
        setError('Could not load cancellation requests - please refresh.');
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setError('');
    setDecidingId(id);
    try {
      await axios.post(`${API}/leave/${id}/${action}`);
      setRequests(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || 'Could not record this decision. Please try again.');
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <div style={{ background: cardBg, border, borderRadius: '10px', padding: '28px', marginBottom: '20px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: 700, color: textCol, margin: '0 0 6px' }}>Cancellation requests awaiting your decision</h3>
      <p style={{ fontSize: '12px', color: mutedCol, margin: '0 0 16px', lineHeight: 1.5 }}>
        These are leaves that were already agreed and that the person now wants to cancel. Until
        you decide, each one still stands and its shared calendar entry remains in place.
      </p>

      {error && <div style={{ fontSize: '13px', color: '#A63A3A', marginBottom: '14px' }} role="alert" aria-live="polite">{error}</div>}

      {loading ? (
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>Loading…</p>
      ) : loadFailed ? null : requests.length === 0 ? (
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>No cancellation requests right now.</p>
      ) : (
        requests.map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: rowBorder, gap: '12px' }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: textCol, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span>{d.employee_name}</span>
                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: WARN_BG, color: WARN_FG, whiteSpace: 'nowrap' }}>
                  Cancellation
                </span>
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: mutedCol }}>
                {d.leave_label} · {fmt(d.start_date)} → {fmt(d.end_date)}
                {d.cancel_reason ? ` · ${d.cancel_reason}` : ''}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => decide(d.id, 'cancel-approve')}
                disabled={decidingId === d.id}
                style={{
                  background: NAVY, color: '#fff', border: `1px solid ${GOLD}`,
                  borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                  cursor: decidingId === d.id ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {decidingId === d.id ? '…' : 'Approve cancellation'}
              </button>
              <button
                onClick={() => decide(d.id, 'cancel-reject')}
                disabled={decidingId === d.id}
                style={{
                  background: 'none', border: `1px solid ${isDarkMode ? 'rgba(166,58,58,0.5)' : '#e0b4b4'}`,
                  color: '#A63A3A', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                  cursor: decidingId === d.id ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {decidingId === d.id ? '…' : 'Keep the leave'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
